/**
 * Daily Sheet (BE-14d-2)
 *
 * One day's postings: the cash strip on top (opening, in, out, closing across bank / cash / e-wallet accounts), the
 * count and amount per entry type, then every entry with its time, party and cash movement. Times are shown in the
 * user's local time (the PDF / CSV print UTC and say so).
 * Route: /reports/daily-sheet
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Grid, TextField, Box, Chip } from '@mui/material';
import ReportPage, { formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { sourceInfo } from '../../utils/accounting/journalSources';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const money = (v) => (Number(v) ? formatCurrency(v) : '-');
const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-');

const TypeChip = ({ type }) => {
  const s = sourceInfo(type);
  return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: '#fff', fontSize: '0.7rem', height: 20 }} />;
};

const DailySheet = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [date, setDate] = useState(toYmd(new Date()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getDailySheet({ date }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the daily sheet'));
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { key: 'reference', label: 'Entry' },
    { key: 'postedAt', label: 'Time', render: (r) => time(r.postedAt) },
    { key: 'sourceType', label: 'Type', render: (r) => <TypeChip type={r.sourceType} /> },
    { key: 'description', label: 'Description', render: (r) => r.description || '-' },
    { key: 'party', label: 'Party', render: (r) => r.party || '-' },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatCurrency(r.amount) },
    { key: 'cashIn', label: 'Cash in', align: 'right', render: (r) => money(r.cashIn), color: '#4caf50' },
    { key: 'cashOut', label: 'Cash out', align: 'right', render: (r) => money(r.cashOut), color: '#f44336' },
  ];

  const cards = report ? [
    { label: 'Opening cash', value: formatCurrency(report.openingCash) },
    { label: 'Cash in', value: formatCurrency(report.cashIn), tone: 'good' },
    { label: 'Cash out', value: formatCurrency(report.cashOut), tone: Number(report.cashOut) > 0 ? 'bad' : undefined },
    { label: 'Closing cash', value: formatCurrency(report.closingCash) },
  ] : [];

  return (
    <ReportPage
      title="Daily Sheet"
      subtitle={`Everything posted on ${formatDate(date)} · ${report?.entryCount ?? 0} entries · cash = bank, cash drawer and e-wallet accounts; times in your local time`}
      exportType="daily-sheet" exportParams={{ date }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={report?.entries || []} rowKey={(r, i) => `${r.reference}-${i}`}
      emptyMessage="Nothing was posted on this day."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Date" type="date" value={date}
              onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      )}
    >
      {!loading && (report?.bySource || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {report.bySource.map((s) => (
            <Chip key={s.method} variant="outlined" size="small"
              label={`${sourceInfo(s.method).label} · ${s.count} · ${formatCurrency(s.amount)}`} sx={{ fontSize: '0.75rem' }} />
          ))}
        </Box>
      )}
    </ReportPage>
  );
};

export default DailySheet;
